/**
 * Where a player gets the client.
 *
 * The room runs in a browser and always will — that is the version nobody has
 * to install, and it is linked from here first for that reason. The
 * applications are for people who play often enough that a window of their own
 * is worth having: it keeps its size, it does not sit in a tab behind
 * something else, and closing the browser does not close the table.
 *
 * The links point at the releases rather than at files on this server. A
 * release is signed, versioned and dated by the thing that built it; a file
 * copied onto a web server is none of those, and a poker client is exactly the
 * sort of download somebody would want to be sure about.
 */

import { SiteHeader, PageTitle } from './Shell.tsx';

/**
 * Where the builds live.
 *
 * The repository is private today, so these links only work for somebody
 * signed in with access to it. Making the releases public — or copying the
 * signed artefacts onto this server — is the step between "the workflow
 * produces installers" and "a stranger can download one", and it is a
 * decision about what to make public rather than a thing to do quietly.
 */
const RELEASES = 'https://github.com/mehdisafari1098-jpg/pokerroom/releases/latest';

interface Build {
  readonly platform: string;
  readonly detail: string;
  readonly note?: string;
  readonly ready: boolean;
}

const BUILDS: readonly Build[] = [
  { platform: 'Windows', detail: 'Windows 10 and later · installer', ready: true },
  { platform: 'macOS', detail: 'Apple silicon and Intel · disk image', ready: true },
  { platform: 'Linux', detail: 'AppImage and .deb', ready: true },
  {
    platform: 'Android',
    detail: 'Android 8 and later · APK',
    note: 'مستقیم نصب می‌شود؛ هنوز روی Google Play نیست.',
    ready: true,
  },
  {
    platform: 'iOS',
    detail: 'iPhone and iPad',
    note: 'ساخته می‌شود ولی هنوز منتشر نشده — انتشار روی App Store به حساب توسعه‌دهنده‌ی اپل نیاز دارد.',
    ready: false,
  },
];

export function Download() {
  return (
    <>
      <SiteHeader here="/download" />
      <div className="page download">
        <PageTitle eyebrow="Iran Poker Tour" title="Get the Client">
          همان اتاق، در پنجره‌ی خودش. نسخه‌ی تحت وب همیشه هست و نصب نمی‌خواهد —
          نرم‌افزار برای کسی است که به‌اندازه‌ای بازی می‌کند که یک پنجره‌ی مستقل بیارزد:
          اندازه‌اش را نگه می‌دارد، پشت تبِ دیگری گم نمی‌شود، و بستن مرورگر میز را نمی‌بندد.
        </PageTitle>

        <a className="dl-web" href="/">
          <div>
            <div className="dl-web-title lat-ui">Play in the browser</div>
            <div className="dl-web-sub">بدون نصب. همین حالا.</div>
          </div>
          <span className="dl-arrow" aria-hidden="true">←</span>
        </a>

        <h2 className="section lat-ui">Downloads</h2>
        <div className="dl-grid">
          {BUILDS.map((build) => (
            <div key={build.platform} className={`dl-card${build.ready ? '' : ' soon'}`}>
              <div className="dl-platform lat-ui">{build.platform}</div>
              <div className="dl-detail lat-ui">{build.detail}</div>
              {build.note !== undefined && <p className="dl-note">{build.note}</p>}
              {build.ready ? (
                <a className="btn-check small" href={RELEASES} rel="noreferrer noopener" target="_blank">
                  Download
                </a>
              ) : (
                <span className="dl-soon lat-ui">Coming</span>
              )}
            </div>
          ))}
        </div>

        <p className="hint dl-fine">
          {/* Said plainly, because a downloaded binary is a thing somebody is
              trusting and they are owed the basis for it. */}
          هر نسخه روی سرورهای ساخت GitHub از روی همان کدی ساخته می‌شود که سایت از آن
          اجرا می‌شود، و از صفحه‌ی Releases دانلود می‌شود — نه از فایلی که کسی روی این
          سرور گذاشته باشد.
        </p>
      </div>
    </>
  );
}
